import { getAppStore } from '@/store';
import renameDriveFile from '@/utils/generation/renameDriveFile';

import type { ContentFile } from '@/types';
import type { BuiltFlowMedia } from '@/utils/generation/buildFlowMediaList';

/**
 * Lands every result of a finished run in the creations list.
 *
 * Both generation pipelines end here — the in-app one (`useGenerationPolling`)
 * and the `presetJsonUrl` deeplink's (`finalizeAutoGeneration`). They used to
 * finish their own way, which is how the deeplink path kept showing one file of
 * a multi-output run: the shape of a resolved result, the index-suffixed
 * rename and the run stamping are the same work, so they are done once here.
 *
 * The run replaces the placeholder **in place**, keeping the slot the user
 * watched spin, and the siblings follow it in arrival order — that order is
 * what `RunCard` cycles and what the lightbox filmstrip lists. One
 * `setContentFiles` for the whole run, so a run of four is one re-render.
 *
 * A result the backend did not store in Drive is dropped unless it is the
 * primary (which falls back to the placeholder's uuid): every action the grid
 * offers — open, delete, react — keys off the Drive uid.
 */
const applyRunResults = async ({
  built,
  placeholderUuid,
  presetTitle,
  runId,
  timestamp,
}: {
  built: BuiltFlowMedia[];
  placeholderUuid: string;
  presetTitle: string;
  /** Stamped on every file so the grid can regroup the run. */
  runId?: string | null;
  timestamp: number;
}): Promise<{ primaryUuid: string }> => {
  // Each result is its own Drive file, so each is renamed on its own. The index
  // suffix keeps the names distinct within a run — without it every file of a
  // multi-output run would be called the same thing.
  const resolved = await Promise.all(
    built.map(
      async (
        { resultFile, resultType, isVideo, isAudio },
        index
      ): Promise<ContentFile | null> => {
        const uuid =
          resultFile.driveUid || (index === 0 ? placeholderUuid : null);
        if (!uuid) return null;

        const { filename } = await renameDriveFile({
          driveUid: resultFile.driveUid,
          presetTitle:
            built.length > 1 ? `${presetTitle} ${index + 1}` : presetTitle,
          resultType,
          timestamp,
        });

        return {
          uuid,
          url: resultFile.url,
          name: filename,
          type: resultFile.type,
          preview: {
            url: isVideo
              ? resultFile.poster
              : isAudio
              ? undefined
              : resultFile.url,
            width: resultFile.width,
            height: resultFile.height,
          },
          isLoading: false,
          generationStartTime: undefined,
          presetType: undefined,
          ...(runId ? { runId } : {}),
        };
      }
    )
  );

  // Read the list only now: the renames above are network calls, and a second
  // generation can prepend its placeholder while they are in flight.
  const { contentFiles, setContentFiles } = getAppStore();
  const placeholder = contentFiles.find(
    (file) => file.uuid === placeholderUuid
  );

  // The placeholder carries what the run knows about itself — the drive
  // attributes and the retry context — and every result inherits it, not just
  // the one that took the placeholder's slot.
  const runFiles = resolved
    .filter((file): file is ContentFile => file !== null)
    .map((file) => ({ ...placeholder, ...file }));
  const runUuids = new Set(runFiles.map((file) => file.uuid));

  // A Drive refetch can land while the renames are in flight and bring in the
  // very files being resolved here; dropping those keeps the run one group.
  const next = contentFiles.flatMap((file) => {
    if (file.uuid === placeholderUuid) return runFiles;
    return runUuids.has(file.uuid) ? [] : [file];
  });
  const hasPlaceholder = contentFiles.some(
    (file) => file.uuid === placeholderUuid
  );

  setContentFiles(hasPlaceholder ? next : [...runFiles, ...next]);

  return { primaryUuid: runFiles[0]?.uuid || placeholderUuid };
};

export default applyRunResults;
